import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Bug, CheckCircle2, HelpCircle, ImagePlus, Lightbulb, MessageSquarePlus, MonitorUp, Trash2, X } from "lucide-react";
import { FEEDBACK_PRIORITIES, FEEDBACK_PRIORITY_LABEL, type CreateFeedbackInput, type FeedbackDto, type FeedbackKind, type FeedbackPriority } from "@theadam/shared";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/misc";
import { ApiError, api } from "@/lib/api";
import { cn } from "@/lib/utils";

export const FEEDBACK_KEY = ["feedback"] as const;

const KINDS: { key: FeedbackKind; label: string; icon: typeof Bug; hint: string }[] = [
  { key: "oneri", label: "Öneri", icon: Lightbulb, hint: "Yeni özellik veya iyileştirme" },
  { key: "hata", label: "Hata", icon: Bug, hint: "Yanlış çalışan bir şey" },
  { key: "soru", label: "Soru", icon: HelpCircle, hint: "Nasıl yapılır?" },
];

/** Görseli en fazla 1600 px genişliğe indirip JPEG'e çevirir (sunucu sınırı 2 MB). */
async function compress(source: Blob | HTMLVideoElement): Promise<Blob> {
  let draw: CanvasImageSource;
  let w: number;
  let h: number;
  if (source instanceof Blob) {
    const bmp = await createImageBitmap(source);
    draw = bmp;
    w = bmp.width;
    h = bmp.height;
  } else {
    draw = source;
    w = source.videoWidth;
    h = source.videoHeight;
  }
  const scale = Math.min(1, 1600 / w);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  canvas.getContext("2d")!.drawImage(draw, 0, 0, canvas.width, canvas.height);
  for (const q of [0.82, 0.7, 0.55, 0.4]) {
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", q));
    if (blob && blob.size < 1.9 * 1024 * 1024) return blob;
  }
  throw new Error("Görsel çok büyük");
}

/** Tarayıcının ekran paylaşımı penceresiyle tek kare yakalar (eklenti gerekmez). */
async function captureScreen(): Promise<Blob> {
  const stream = await navigator.mediaDevices.getDisplayMedia({ video: { displaySurface: "browser" }, audio: false, preferCurrentTab: true } as DisplayMediaStreamOptions);
  try {
    const video = document.createElement("video");
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    // İzin penceresi kapanıp sayfa yeniden çizilsin.
    await new Promise((r) => setTimeout(r, 350));
    return await compress(video);
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
}

const canCapture = typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getDisplayMedia);

function FeedbackDialog({ onClose, onHide }: { onClose: () => void; onHide: (hidden: boolean) => void }) {
  const location = useLocation();
  const qc = useQueryClient();
  const page = `${location.pathname}${location.search}`;
  const [kind, setKind] = useState<FeedbackKind>("oneri");
  const [priority, setPriority] = useState<FeedbackPriority>("orta");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [shot, setShot] = useState<Blob | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<FeedbackDto | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => titleRef.current?.focus(), []);
  useEffect(() => {
    if (!shot) return setPreview(null);
    const url = URL.createObjectURL(shot);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [shot]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    // Panodan yapıştırma: Win+Shift+S ile alınan ekran görüntüsü doğrudan eklenir.
    const onPaste = (e: ClipboardEvent) => {
      const file = [...(e.clipboardData?.files ?? [])].find((f) => f.type.startsWith("image/"));
      if (!file) return;
      e.preventDefault();
      void attach(file);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("paste", onPaste);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function attach(file: Blob) {
    setError(null);
    try {
      setShot(await compress(file));
    } catch {
      setError("Görsel okunamadı. JPG veya PNG deneyin.");
    }
  }

  async function capture() {
    setError(null);
    onHide(true);
    try {
      setShot(await captureScreen());
    } catch (err) {
      if (!(err instanceof DOMException && err.name === "NotAllowedError")) setError("Ekran yakalanamadı. Görsel seçebilir veya yapıştırabilirsiniz.");
    } finally {
      onHide(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (title.trim().length < 3) {
      setError("Kısa bir başlık yazın.");
      titleRef.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: CreateFeedbackInput = { kind, priority, title: title.trim(), description: description.trim(), page, viewport: `${window.innerWidth}×${window.innerHeight}` };
      const created = await api.post<FeedbackDto>("/feedback", body);
      if (shot) {
        try {
          await api.upload(`/uploads/feedback/${created.id}/screenshot`, shot);
        } catch {
          /* kayıt oluştu; görsel yüklenemese de bildirim kaybolmasın */
        }
      }
      await qc.invalidateQueries({ queryKey: FEEDBACK_KEY });
      setDone(created);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Gönderilemedi. Lütfen tekrar deneyin.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="fb-title">
      <button type="button" className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" aria-label="Kapat" onClick={onClose} />
      <div className="relative max-h-[92dvh] w-full max-w-lg overflow-y-auto rounded-t-3xl border bg-card shadow-2xl sm:rounded-3xl">
        <div className="flex items-start justify-between gap-3 border-b px-6 pt-5 pb-4">
          <div>
            <h2 id="fb-title" className="text-lg font-semibold">
              {done ? "Teşekkürler" : "Öneri / Hata bildir"}
            </h2>
            {!done && <p className="mt-0.5 text-sm text-muted-foreground">Bulunduğunuz sayfa ve tarayıcı bilgisi otomatik eklenir.</p>}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Kapat">
            <X />
          </Button>
        </div>

        {done ? (
          <div className="space-y-5 px-6 py-6">
            <div className="flex gap-3">
              <CheckCircle2 className="mt-0.5 size-6 shrink-0 text-success" aria-hidden />
              <div>
                <p className="font-medium">Kaydınız alındı.</p>
                <p className="mt-1 text-sm text-muted-foreground">Durumu ve yanıtı Ayarlar → Öneriler bölümünden izleyebilirsiniz.</p>
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Kapat
              </Button>
              <Button asChild>
                <Link to={`/ayarlar?sekme=oneriler&kayit=${done.id}`} onClick={onClose}>
                  Kaydı görüntüle
                </Link>
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5 px-6 py-5" noValidate>
            {error && <Alert tone="danger" title={error} />}

            <div role="radiogroup" aria-label="Tür" className="grid grid-cols-3 gap-2">
              {KINDS.map((k) => (
                <button
                  key={k.key}
                  type="button"
                  role="radio"
                  aria-checked={kind === k.key}
                  onClick={() => setKind(k.key)}
                  className={cn(
                    "flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-center transition-colors",
                    kind === k.key ? "border-accent bg-accent/10 ring-2 ring-accent/25" : "hover:border-accent/50 hover:bg-muted/50",
                  )}
                >
                  <k.icon className={cn("size-5", kind === k.key ? "text-accent" : "text-muted-foreground")} aria-hidden />
                  <span className="text-sm font-semibold">{k.label}</span>
                  <span className="hidden text-[11px] leading-tight text-muted-foreground sm:block">{k.hint}</span>
                </button>
              ))}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="fb-t" className="text-sm font-medium">
                Başlık
              </label>
              <input
                id="fb-t"
                ref={titleRef}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={140}
                placeholder={kind === "hata" ? "Ör. Etiket yazdırınca kargo kodu çıkmıyor" : kind === "soru" ? "Ör. Toplu stok nasıl güncellenir?" : "Ör. Siparişleri Excel'e aktarabilelim"}
                className="flex h-11 w-full rounded-xl border border-input bg-background/60 px-3.5 text-sm outline-none focus:border-accent focus:ring-4 focus:ring-accent/15"
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="fb-d" className="text-sm font-medium">
                Ayrıntı <span className="font-normal text-muted-foreground">(isteğe bağlı)</span>
              </label>
              <textarea
                id="fb-d"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={4000}
                rows={4}
                placeholder={kind === "hata" ? "Ne yaptınız, ne bekliyordunuz, ne oldu? Sipariş numarası varsa yazın." : "Ne istediğinizi ve neden işinize yarayacağını kısaca anlatın."}
                className="w-full resize-y rounded-xl border border-input bg-background/60 px-3.5 py-2.5 text-sm outline-none focus:border-accent focus:ring-4 focus:ring-accent/15"
              />
            </div>

            <div className="space-y-1.5">
              <span className="text-sm font-medium">Önem</span>
              <div role="radiogroup" aria-label="Önem" className="flex flex-wrap gap-1.5">
                {[...FEEDBACK_PRIORITIES].reverse().map((p) => (
                  <button
                    key={p}
                    type="button"
                    role="radio"
                    aria-checked={priority === p}
                    onClick={() => setPriority(p)}
                    className={cn("rounded-full border px-3 py-1 text-sm transition-colors", priority === p ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted")}
                  >
                    {FEEDBACK_PRIORITY_LABEL[p]}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <span className="text-sm font-medium">Ekran görüntüsü <span className="font-normal text-muted-foreground">(isteğe bağlı)</span></span>
              {preview ? (
                <div className="relative overflow-hidden rounded-xl border bg-muted/40">
                  <img src={preview} alt="Eklenen ekran görüntüsü" className="max-h-56 w-full object-contain" />
                  <Button type="button" variant="outline" size="sm" className="absolute top-2 right-2 bg-card/95" onClick={() => setShot(null)}>
                    <Trash2 /> Kaldır
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {canCapture && (
                    <Button type="button" variant="outline" size="sm" onClick={capture}>
                      <MonitorUp /> Ekranı yakala
                    </Button>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                    <ImagePlus /> Görsel seç
                  </Button>
                  <span className="self-center text-xs text-muted-foreground">veya Ctrl+V ile yapıştırın</span>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void attach(f);
                      e.target.value = "";
                    }}
                  />
                </div>
              )}
            </div>

            <p className="truncate rounded-lg bg-muted/50 px-3 py-2 font-mono text-xs text-muted-foreground" title={page}>
              Sayfa: {page}
            </p>

            <div className="flex justify-end gap-2 border-t pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Vazgeç
              </Button>
              <Button type="submit" loading={busy}>
                Gönder
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/** Her sayfanın sağ altında: öneri, hata veya soru bildirimi. */
export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  return createPortal(
    <div className={cn(hidden && "invisible")}>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group fixed right-4 bottom-4 z-40 flex h-11 items-center gap-2 rounded-full border border-accent/30 bg-primary pr-4 pl-3 text-sm font-medium text-primary-foreground shadow-lg shadow-primary/25 transition-transform hover:-translate-y-0.5 focus-visible:ring-4 focus-visible:ring-accent/40 focus-visible:outline-none sm:right-6 sm:bottom-6 print:hidden"
          aria-label="Öneri veya hata bildir"
        >
          <MessageSquarePlus className="size-[18px] text-accent" aria-hidden />
          <span className="hidden sm:inline">Öneri / Hata</span>
        </button>
      )}
      {open && <FeedbackDialog onClose={() => setOpen(false)} onHide={setHidden} />}
    </div>,
    document.body,
  );
}
